import { useState } from "react";
import { useEffect } from "react";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import EventCard from "@/components/feature/EventCard";
import { events } from "@/mocks/events";
import { categories } from "@/mocks/site";

function parseHashQuery(hash: string) {
  if (!hash.startsWith("#gua")) return { cat: "all", q: "" };
  const qs = hash.includes("?") ? hash.slice(hash.indexOf("?") + 1) : "";
  const sp = new URLSearchParams(qs);
  return { cat: sp.get("cat") || "all", q: sp.get("q") || "" };
}

export default function GuaSection() {
  const location = useLocation();
  const initial = parseHashQuery(location.hash);
  const [cat, setCat] = useState(initial.cat);
  const [q, setQ] = useState(initial.q);

  useEffect(() => {
    const p = parseHashQuery(location.hash);
    if (location.hash.startsWith("#gua")) {
      setCat(p.cat);
      setQ(p.q);
    }
  }, [location.hash]);

  const filtered = useMemo(() => {
    const kw = q.trim().toLowerCase();
    return events.filter((e) => {
      const okCat = cat === "all" || e.catId === cat;
      if (!okCat) return false;
      if (!kw) return true;
      return (
        e.title.toLowerCase().includes(kw) ||
        e.summary.toLowerCase().includes(kw) ||
        e.catName.toLowerCase().includes(kw) ||
        e.tags.some((t) => t.toLowerCase().includes(kw))
      );
    });
  }, [cat, q]);

  return (
    <section id="gua" className="border-b border-background-200 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-primary-600">
              <i className="ri-newspaper-line" />
              This Week's Melons
            </p>
            <h2 className="mt-2 font-display text-2xl font-black text-foreground-950 md:text-4xl">
              本期瓜单
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600 md:text-base">
              正在发酵、持续跟进、收官复盘 —— 每个大瓜都有完整时间线等你开吃。
            </p>
          </div>
          <p className="text-sm text-foreground-500">共 <b className="text-primary-700">{filtered.length}</b> 条在榜</p>
        </div>

        {/* controls */}
        <div className="mt-7 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setCat("all")}
              className={`inline-flex whitespace-nowrap items-center rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                cat === "all" ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              全部
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCat(c.id)}
                className={`inline-flex whitespace-nowrap items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                  cat === c.id ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
                }`}
              >
                <i className={`${c.icon} text-[15px]`} />
                {c.name}
              </button>
            ))}
          </div>

          <label className="relative lg:w-72">
            <span className="sr-only">搜索本期瓜单</span>
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="搜标题 / 关键词 / 专区…"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
            {q && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-6 w-6 items-center justify-center rounded-full text-foreground-400 hover:bg-background-100 hover:text-foreground-700 cursor-pointer"
              >
                <i className="ri-close-line text-sm" />
              </button>
            )}
          </label>
        </div>

        {/* grid */}
        {filtered.length ? (
          <div className="mt-7 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((e) => (
              <EventCard key={e.id} event={e} />
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center rounded-2xl border border-dashed border-background-300 bg-background-100/50 px-6 py-14 text-center">
            <i className="ri-fridge-line text-4xl text-foreground-300" />
            <p className="mt-4 font-display text-lg font-bold text-foreground-800">这片瓜地暂时空着</p>
            <p className="mt-1 text-sm text-foreground-500">没找到匹配的瓜，换个关键词或专区试试。</p>
            <button
              type="button"
              onClick={() => {
                setCat("all");
                setQ("");
              }}
              className="mt-5 inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-primary-500 px-5 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-refresh-line" />
              清空筛选
            </button>
          </div>
        )}
      </div>
    </section>
  );
}